import { DatePicker, Form, Grid, PhoneField, Select, Text, TextField } from '@approvalmax/ui/src/components';
import { memo, useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { PatternFormat } from 'react-number-format';
import { CountryIsoCode } from 'shared/data/walletsKyc/v1';
import { usePersonCountries } from 'shared/hooks/(wallets)/kyc/usePersonCountries';
import { z } from 'zod';

import { eighteenYearsEarlier } from './PersonVerificationDetails.constants';
import { messages } from './PersonVerificationDetails.messages';
import { schema } from './PersonVerificationDetails.schema';

export const PersonVerificationDetails = memo(() => {
    const { countries, isCountriesLoading } = usePersonCountries();

    const { setValue } = useFormContext<z.infer<typeof schema>>();
    const country = useWatch<z.infer<typeof schema>, 'person.address.country'>({
        name: 'person.address.country',
    });

    const handleCountryChange = useCallback(
        (value: string) => {
            setValue('person.isUs', value === CountryIsoCode.US);
        },
        [setValue]
    );

    return (
        <Form.Part>
            <Grid gap={16}>
                <Text font='headline' fontSize='xxsmall' fontWeight='medium'>
                    {messages.verificationDetails}
                </Text>

                <Text font='body' fontSize='medium' fontWeight='regular'>
                    {messages.weNeedDetails}
                </Text>

                <DatePicker
                    required
                    clearable={false}
                    defaultView='decade'
                    name='person.dob'
                    label={messages.dateOfBirth}
                    placeholder={messages.dateOfBirthPlaceholder}
                    maxDate={eighteenYearsEarlier()}
                    activeStartDate={eighteenYearsEarlier()}
                />

                <Grid gap={16} gridTemplateColumns='1fr 1fr'>
                    <TextField
                        required
                        clearable={false}
                        name='person.address.street'
                        label={messages.street}
                        placeholder={messages.streetPlaceholder}
                    />

                    <TextField
                        required
                        clearable={false}
                        name='person.address.buildingNumber'
                        label={messages.buildingNumber}
                        placeholder={messages.buildingNumberPlaceholder}
                    />
                </Grid>

                <TextField
                    name='person.address.apartmentNumber'
                    label={messages.apNumber}
                    placeholder={messages.apNumberPlaceholder}
                />

                <TextField
                    required
                    clearable={false}
                    name='person.address.buildingName'
                    label={messages.buildingName}
                    placeholder={messages.buildingNamePlaceholder}
                />

                <TextField name='person.address.state' label={messages.state} placeholder={messages.statePlaceholder} />

                <Grid gap={16} gridTemplateColumns='1fr 1fr'>
                    <TextField
                        required
                        clearable={false}
                        name='person.address.city'
                        label={messages.city}
                        placeholder={messages.cityPlaceholder}
                    />

                    <TextField
                        required
                        clearable={false}
                        name='person.address.postalCode'
                        label={messages.postalCode}
                        placeholder={messages.postalCodePlaceholder}
                    />
                </Grid>

                <Select
                    required
                    autocomplete
                    clearable={false}
                    name='person.address.country'
                    label={messages.country}
                    placeholder={messages.countryPlaceholder}
                    items={countries}
                    progress={isCountriesLoading}
                    onChange={handleCountryChange}
                />

                <Grid gap={16} gridTemplateColumns='1fr 1fr'>
                    <PhoneField
                        required
                        name='person.phoneNumber'
                        label={messages.phone}
                        placeholder={messages.phonePlaceholder}
                        defaultCountry={country}
                    />

                    {country === CountryIsoCode.US && (
                        <TextField
                            required
                            as={PatternFormat}
                            clearable={false}
                            name='person.idNumber'
                            label={messages.idNumber}
                            placeholder={messages.idNumberPlaceholder}
                            format='###-##-####'
                        />
                    )}
                </Grid>
            </Grid>
        </Form.Part>
    );
});

PersonVerificationDetails.displayName = 'PersonVerificationDetails';
